/**
 * Utility functions for making fetch requests with JSON handling.
 * Provides a consistent interface for API calls with proper error handling.
 */

type FetchJsonOptions = Omit<RequestInit, "body"> & {
  body?: any;
  errorPrefix?: string;
};

/**
 * Generic fetch wrapper with JSON parsing and error handling
 */
async function fetchJson<T = any>(
  url: string,
  options: FetchJsonOptions = {},
): Promise<T> {
  const { body, headers, errorPrefix, ...restOptions } = options;

  const config: RequestInit = {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    ...restOptions,
  };

  if (body !== undefined) {
    config.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  const response = await fetch(url, config);

  if (!response.ok) {
    const prefix = errorPrefix || "Request failed";
    throw new Error(`${prefix} (${response.status})`);
  }

  // Handle 204 No Content responses
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Performs a GET request with JSON parsing
 */
export async function getJson<T = any>(
  url: string,
  options?: Omit<FetchJsonOptions, "method" | "body">,
): Promise<T> {
  return fetchJson<T>(url, {
    ...options,
    method: "GET",
    errorPrefix: options?.errorPrefix || "Failed to fetch data",
  });
}

/**
 * Performs a POST request with JSON body
 */
export async function postJson<T = any>(
  url: string,
  body: any,
  options?: Omit<FetchJsonOptions, "method" | "body">,
): Promise<T> {
  return fetchJson<T>(url, {
    ...options,
    method: "POST",
    body,
    errorPrefix: options?.errorPrefix || "Failed to create resource",
  });
}

/**
 * Performs a DELETE request
 */
export async function deleteJson<T = any>(
  url: string,
  options?: Omit<FetchJsonOptions, "method" | "body">,
): Promise<T> {
  return fetchJson<T>(url, {
    ...options,
    method: "DELETE",
    errorPrefix: options?.errorPrefix || "Failed to delete resource",
  });
}
